/**
 * Authenticated campaign realtime projection boundary.
 *
 * WHO: The keyed campaign route provider owns one socket and exposes accepted state to CampaignPage
 *       and SessionView; backend membership and the verified AuthContext account remain authority.
 * WHAT: Validates campaign/session/roster/history snapshot identity, revision and bounded schemas
 *       before installing it into shared contexts. History is a curated <=50-entry preview only;
 *       older pages use the separate authorized CampaignHistoryService HTTP path.
 * WHERE: CampaignRealtimeProvider is mounted by App.tsx route wrappers and consumes webSocketService
 *        envelopes plus SessionContext/CampaignContext projection actions.
 * WHEN: Generation, campaign/account scope, event ID and server revision fence callbacks; account or
 *       campaign changes hide prior snapshots during render, before asynchronous effect cleanup.
 * WHY: A realtime payload is untrusted transport data, not authorization or a replacement for saved
 *      HTTP records. Strict validation and scope fencing prevent stale or malformed state from leaking.
 */
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Alert, AlertDescription, AlertTitle, Box, Button } from '@chakra-ui/react';
import { useAuth } from './AuthContext';
import { useSession } from './SessionContext';
import { useCampaign } from './CampaignContext';
import { webSocketService, type RealtimeConnectionStatus, type RealtimeEnvelope } from '../services/websocket/websocketService';
import type { SessionState } from '../services/api/SessionService';
import type { CampaignHistoryEntry } from '../services/api/CampaignHistoryService';

interface CampaignSnapshot {
  campaign: { campaign_id: string; campaign_name: string; current_session_id: string | null; is_session_active: boolean };
  session: null | {
    session_id: string; campaign_id: string; status: SessionState['status']; state: SessionState['status']; started_at: string | null; lifecycle_revision: number;
    elapsed_minutes: number; elapsed_seconds: number; master_mode_enabled: boolean;
    can_pause: boolean; can_resume: boolean; paused_by_me: boolean; participants: NonNullable<SessionState['participants']>;
  };
  roster: Array<{ user_id: string; screen_name: string; role: string }>;
  /** Curated, bounded server projection; older HTTP pages are loaded separately. */
  history: CampaignHistoryEntry[];
  history_has_more: boolean;
}
interface RealtimeContextValue {
  status: RealtimeConnectionStatus;
  snapshot: CampaignSnapshot | null;
  retry: () => void;
  /** Request an authoritative campaign projection; callers still require a deliberate action after refresh. */
  refreshSnapshot: () => void;
  reportUnsent: () => void;
  pinnedSessionChanged: boolean;
}
const CampaignRealtimeContext = createContext<RealtimeContextValue | undefined>(undefined);

const HISTORY_NOTE_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Validate the bounded 003 snapshot projection before it reaches session UI. */
function isValidHistory(entries: unknown[], hasMore: unknown): entries is CampaignHistoryEntry[] {
  if (entries.length > 50 || (hasMore !== undefined && typeof hasMore !== 'boolean')) return false;
  const ids = new Set<string>();
  const sessions = new Set<string>();
  for (const raw of entries) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return false;
    const entry = raw as Record<string, unknown>;
    // Keep the websocket projection aligned with the six-field history API contract. Unknown keys
    // are rejected so transport additions cannot expose private metadata through shared context.
    const expectedFields = ['author', 'created_at', 'entry_id', 'session_id', 'text', 'type'];
    if (Object.keys(entry).length !== expectedFields.length || Object.keys(entry).some((key) => !expectedFields.includes(key))) return false;
    if (typeof entry.entry_id !== 'string' || !entry.entry_id || ids.has(entry.entry_id) ||
        typeof entry.session_id !== 'string' || !entry.session_id ||
        (entry.type !== 'session_note' && entry.type !== 'session_summary') ||
        typeof entry.text !== 'string' || Array.from(entry.text).length > (entry.type === 'session_note' ? 4000 : 5000) ||
        (entry.type === 'session_note' && (!entry.text.trim() || !HISTORY_NOTE_ID.test(entry.entry_id))) ||
        (entry.type === 'session_summary' && (entry.entry_id !== `summary:${entry.session_id}` || entry.author !== 'Session summary')) ||
        typeof entry.author !== 'string' || !entry.author.trim() ||
        typeof entry.created_at !== 'string' || !Number.isFinite(Date.parse(entry.created_at))) return false;
    ids.add(entry.entry_id);
    sessions.add(entry.session_id);
    if (sessions.size > 5) return false;
  }
  return true;
}

/** Owns one authenticated campaign socket for this keyed route subtree. All callbacks are fenced by this provider's generation and scope. */
export const CampaignRealtimeProvider: React.FC<{ campaignId?: string; expectedSessionId?: string; children: React.ReactNode }> = ({ campaignId, expectedSessionId, children }) => {
  const { isAuthenticated, userInfo } = useAuth();
  const { applyRealtimeState, applyRealtimeCampaignCursor, refreshState } = useSession();
  // SessionContext refresh callbacks follow the loaded session object. Keep the live callback
  // available without reconnecting the campaign transport when that object is initialized.
  const refreshStateRef = useRef(refreshState);
  refreshStateRef.current = refreshState;
  const { applyRealtimeCampaignProjection } = useCampaign();
  const [status, setStatus] = useState<RealtimeConnectionStatus>('unavailable');
  const [snapshotState, setSnapshotState] = useState<{ scopeKey: string; value: CampaignSnapshot } | null>(null);
  // A render-time key hides the previous authorized projection immediately on account/campaign
  // changes, before effect cleanup runs; that prevents one frame of cross-scope history leakage.
  const snapshotScopeKey = `${campaignId ?? ''}\u0000${userInfo?.id ?? ''}\u0000${isAuthenticated}`;
  const snapshot = snapshotState?.scopeKey === snapshotScopeKey ? snapshotState.value : null;
  const [unsent, setUnsent] = useState(false);
  const [unsentReason, setUnsentReason] = useState<'connection' | 'unsupported' | null>(null);
  const [pinnedSessionChanged, setPinnedSessionChanged] = useState(false);
  const pinnedMismatchRefresh = useRef(false);
  const generation = useRef(0);
  const revision = useRef(-1);
  const seenEvents = useRef(new Set<string>());
  const highestPendingRevision = useRef<number | null>(null);
  const refreshTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  const retry = useCallback(() => webSocketService.retry(), []);
  const refreshSnapshot = useCallback(() => { webSocketService.requestStateRefresh(); }, []);
  const reportUnsent = useCallback((reason: 'connection' | 'unsupported' = 'connection') => { setUnsent(true); setUnsentReason(reason); }, []);

  useEffect(() => {
    const scope = ++generation.current;
    revision.current = -1;
    seenEvents.current.clear();
    highestPendingRevision.current = null;
    pinnedMismatchRefresh.current = false;
    setPinnedSessionChanged(false);
    setSnapshotState(null);
    setUnsent(false);
    setUnsentReason(null);
    if (!isAuthenticated || !userInfo?.id || !campaignId) {
      webSocketService.disconnect();
      setStatus('unavailable');
      return () => { generation.current += 1; };
    }

    const current = () => generation.current === scope;
    const onStatus = (value: { status: RealtimeConnectionStatus }) => { if (current()) setStatus(value.status); };
    const onChanged = (envelope: RealtimeEnvelope) => {
      if (!current() || envelope.campaign_id !== campaignId || envelope.server_revision <= revision.current) return;
      highestPendingRevision.current = Math.max(highestPendingRevision.current ?? -1, envelope.server_revision);
      // `state.changed` carries no domain data; it is only a hint to fetch the committed projection.
      webSocketService.requestStateRefresh();
    };
    const onSnapshot = (envelope: RealtimeEnvelope) => {
      if (!current() || envelope.campaign_id !== campaignId || envelope.server_revision < revision.current) return;
      if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(envelope.event_id)) return;
      const data = envelope.data as Partial<CampaignSnapshot>;
      if (!data.campaign || data.campaign.campaign_id !== campaignId || typeof data.campaign.campaign_name !== 'string' ||
          !(data.campaign.current_session_id === null || (typeof data.campaign.current_session_id === 'string' && data.campaign.current_session_id.length > 0)) ||
          typeof data.campaign.is_session_active !== 'boolean' || data.campaign.current_session_id !== envelope.session_id ||
          !Array.isArray(data.roster) || !Array.isArray(data.history) || !isValidHistory(data.history, data.history_has_more) || !Object.prototype.hasOwnProperty.call(data, 'session')) return;
      if (data.session !== null && (!data.session || typeof data.session !== 'object' || data.session.campaign_id !== campaignId || data.session.session_id !== envelope.session_id || !Number.isSafeInteger(data.session.lifecycle_revision) || data.session.lifecycle_revision < 0 || data.session.state !== data.session.status || !['scheduled', 'active', 'paused', 'ended'].includes(data.session.status) || !(data.session.started_at === null || typeof data.session.started_at === 'string') || !Number.isSafeInteger(data.session.elapsed_minutes) || data.session.elapsed_minutes < 0 || !Number.isSafeInteger(data.session.elapsed_seconds) || data.session.elapsed_seconds < 0 || typeof data.session.master_mode_enabled !== 'boolean' || typeof data.session.can_pause !== 'boolean' || typeof data.session.can_resume !== 'boolean' || typeof data.session.paused_by_me !== 'boolean' || !Array.isArray(data.session.participants))) return;
      if (!data.session && envelope.session_id !== null) return;
      if (data.campaign.is_session_active && (!data.session || !['active', 'paused'].includes(data.session.status))) return;
      if (data.roster.some((member) => !member || typeof member.user_id !== 'string' || typeof member.screen_name !== 'string' || !['gm', 'player', 'spectator'].includes(member.role))) return;
      // The same event can be replayed as the first snapshot after reconnect. Ignore it
      // during a healthy ready stream, but let it complete synchronization for this
      // newly authorized socket so an unchanged campaign cannot remain stuck syncing.
      const duplicateDuringSync = seenEvents.current.has(envelope.event_id) && webSocketService.getConnectionStatus() !== 'synchronizing';
      if (duplicateDuringSync) return;
      const wanted = highestPendingRevision.current;
      if (wanted !== null && envelope.server_revision < wanted) {
        webSocketService.requestStateRefresh();
        return;
      }
      revision.current = envelope.server_revision;
      highestPendingRevision.current = null;
      seenEvents.current.add(envelope.event_id);
      if (seenEvents.current.size > 256) seenEvents.current = new Set(Array.from(seenEvents.current).slice(-128));
      const accepted = { ...data, history_has_more: data.history_has_more === true } as CampaignSnapshot;
      setSnapshotState({ scopeKey: snapshotScopeKey, value: accepted });
      // The cursor tuple is the user's compare-and-set precondition for the next Start.
      // Keep campaign ordering here separate from session.lifecycle_revision used by writes.
      applyRealtimeCampaignCursor({
        campaign_id: campaignId,
        current_session_id: accepted.campaign.current_session_id,
        is_session_active: accepted.campaign.is_session_active,
        server_revision: envelope.server_revision,
      });
      const snapshotSessionId = accepted.session?.session_id ?? null;
      const pinnedMismatch = Boolean(expectedSessionId && snapshotSessionId !== expectedSessionId);
      setPinnedSessionChanged(pinnedMismatch);
      applyRealtimeCampaignProjection(campaignId, { campaign_name: accepted.campaign.campaign_name, participant_count: accepted.roster.length });
      if (pinnedMismatch) {
        // A campaign's current session may move on while this URL remains pinned to an older session.
        // Refresh only that pinned ID through SessionContext; never install the newer session here.
        if (!pinnedMismatchRefresh.current) {
          pinnedMismatchRefresh.current = true;
          void refreshStateRef.current();
        }
      } else if (accepted.session) {
        pinnedMismatchRefresh.current = false;
        const session = accepted.session;
        const sessionState: SessionState = {
          session_id: session.session_id, campaign_id: campaignId, status: session.status,
          state: session.state === 'active' || session.state === 'paused' || session.state === 'ended' ? session.state : undefined,
          started_at: session.started_at, lifecycle_revision: session.lifecycle_revision, elapsed_minutes: session.elapsed_minutes,
          elapsed_seconds: session.elapsed_seconds, master_mode_enabled: session.master_mode_enabled,
          can_pause: session.can_pause, can_resume: session.can_resume,
          paused_by_me: session.paused_by_me, participants: session.participants,
        };
        applyRealtimeState(sessionState);
      } else {
        pinnedMismatchRefresh.current = false;
        applyRealtimeState(null);
      }
      webSocketService.markSynchronized();
    };
    const onHandshake = () => { if (current()) setStatus('synchronizing'); };
    const onFailure = () => { if (current()) setStatus('reconnect_exhausted'); };
    const onSendFailed = (failure: { reason?: string }) => { if (current()) { setUnsent(true); setUnsentReason(failure.reason === 'unsupported' ? 'unsupported' : 'connection'); } };

    webSocketService.on('connection_status', onStatus);
    webSocketService.on('handshake_validated', onHandshake);
    webSocketService.on('state.changed', onChanged);
    webSocketService.on('state.snapshot', onSnapshot);
    webSocketService.on('connection_failed', onFailure);
    webSocketService.on('send_failed', onSendFailed);
    webSocketService.connect(campaignId);
    refreshTimer.current = setInterval(() => { if (current() && webSocketService.getConnectionStatus() === 'ready') webSocketService.requestStateRefresh(); }, 5000);

    return () => {
      generation.current += 1;
      if (refreshTimer.current) clearInterval(refreshTimer.current);
      refreshTimer.current = null;
      webSocketService.off('connection_status', onStatus);
      webSocketService.off('handshake_validated', onHandshake);
      webSocketService.off('state.changed', onChanged);
      webSocketService.off('state.snapshot', onSnapshot);
      webSocketService.off('connection_failed', onFailure);
      webSocketService.off('send_failed', onSendFailed);
      applyRealtimeCampaignCursor(null);
      webSocketService.disconnect();
    };
  }, [campaignId, expectedSessionId, snapshotScopeKey, isAuthenticated, userInfo?.id, applyRealtimeState, applyRealtimeCampaignCursor, applyRealtimeCampaignProjection]);

  const value = { status, snapshot, retry, refreshSnapshot, reportUnsent, pinnedSessionChanged };
  const labels: Record<RealtimeConnectionStatus, string> = {
    connecting: 'Connecting to campaign', authenticating: 'Authenticating campaign access',
    synchronizing: 'Synchronizing current campaign state', ready: 'Campaign is up to date',
    retrying: 'Reconnecting to campaign', reconnect_exhausted: 'Campaign connection needs a retry',
    unavailable: 'Campaign connection unavailable',
  };
  const showStatus = status !== 'ready' || unsent || pinnedSessionChanged;
  return <CampaignRealtimeContext.Provider value={value}>
    {showStatus && <Alert status={unsent || status === 'reconnect_exhausted' || status === 'unavailable' ? 'warning' : 'info'} mb={4} data-testid="campaign-realtime-status">
      <Box flex="1"><AlertTitle>{unsent ? (unsentReason === 'unsupported' ? 'Action unavailable' : 'Action not sent') : pinnedSessionChanged ? 'This view is pinned to an older session' : labels[status]}</AlertTitle>
        <AlertDescription>{unsent ? (unsentReason === 'unsupported' ? 'This action is unavailable on this connection. Your draft is kept.' : 'Your action was not sent. Review it and resubmit it explicitly when the campaign is ready.') : pinnedSessionChanged ? 'The campaign now has a different current session. This view will keep the selected session and refresh its saved state.' : status === 'reconnect_exhausted' || status === 'unavailable' ? 'Current campaign state may be unavailable.' : labels[status]}</AlertDescription>
      </Box>
      {(status === 'reconnect_exhausted' || status === 'unavailable' || unsent) && <Button size="sm" onClick={() => { if (unsent && (unsentReason === 'unsupported' || status === 'ready')) { setUnsent(false); setUnsentReason(null); } else retry(); }}>{unsent && (unsentReason === 'unsupported' || status === 'ready') ? 'Dismiss' : 'Retry connection'}</Button>}
    </Alert>}
    {children}
  </CampaignRealtimeContext.Provider>;
};

export const useOptionalCampaignRealtime = () => useContext(CampaignRealtimeContext);

export const useCampaignRealtime = () => {
  const value = useOptionalCampaignRealtime();
  if (!value) throw new Error('useCampaignRealtime must be used within CampaignRealtimeProvider');
  return value;
};
